/**
 * Stockist dealer-locator accessibility helpers.
 *
 * - Clarify website link names (bare domains → "{Brand} website")
 * - Drop autocomplete option aria-labels that diverge from visible text (Label in Name)
 * - Mark Google Maps keyboard-shortcuts layout table as presentational
 * Re-runs when Stockist/Maps mutate the widget DOM.
 */
(function () {
  const WIDGET = '#stockist-widget';
  const WEBSITE_LINK = 'a.stockist-feature-color[href^="http"]';
  const MAP_TABLE = '#stockist-widget .LGLeeN-keyboard-shortcuts-view table';
  const AUTOCOMPLETE_OPTION = '#stockist-widget .stockist-autocomplete-item[role="option"]';

  /**
   * @param {HTMLAnchorElement} anchor
   * @returns {string|null}
   */
  function readableSiteName(anchor) {
    try {
      const url = new URL(anchor.href);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
      // Leave Stockist's "Directions" Google Maps links alone.
      if (url.hostname.includes('google.') && url.pathname.includes('/maps')) return null;

      const host = url.hostname.replace(/^www\./i, '');
      const brand = host.split('.')[0] || host;
      if (!brand) return null;

      return brand.charAt(0).toUpperCase() + brand.slice(1) + ' website';
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {HTMLAnchorElement} link
   */
  function ensureNoreferrer(link) {
    if (link.target !== '_blank') return;
    const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
    rel.add('noopener');
    rel.add('noreferrer');
    link.setAttribute('rel', Array.from(rel).join(' '));
  }

  function updateWebsiteLinks() {
    document.querySelectorAll(WEBSITE_LINK).forEach((link) => {
      if (link.dataset.a11yStockistLabeled === 'true') return;

      const label = readableSiteName(link);
      if (!label) return;

      link.textContent = label;
      link.setAttribute('aria-label', label);
      link.dataset.a11yStockistLabeled = 'true';
      ensureNoreferrer(link);
    });
  }

  /**
   * Stockist sets aria-label that diverges from option textContent (extra commas
   * and/or different spacing than primary+secondary spans). Removing it lets the
   * accessible name come from the visible text (Label in Name).
   */
  function syncAutocompleteOptionLabels() {
    document.querySelectorAll(AUTOCOMPLETE_OPTION).forEach((item) => {
      if (!item.hasAttribute('aria-label')) return;
      item.removeAttribute('aria-label');
    });
  }

  function fixMapKeyboardShortcutsTable() {
    document.querySelectorAll(MAP_TABLE).forEach((table) => {
      if (table.getAttribute('role') === 'presentation') return;
      table.setAttribute('role', 'presentation');
    });
  }

  function enhance() {
    updateWebsiteLinks();
    syncAutocompleteOptionLabels();
    fixMapKeyboardShortcutsTable();
  }

  function init() {
    enhance();

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        enhance();
      });
    });

    const observe = (target) => {
      observer.observe(target, { childList: true, subtree: true });
    };

    const widget = document.querySelector(WIDGET);
    if (widget) {
      observe(widget);
      return;
    }

    observe(document.documentElement);
    const boot = new MutationObserver(() => {
      const el = document.querySelector(WIDGET);
      if (!el) return;
      boot.disconnect();
      observer.disconnect();
      observe(el);
      enhance();
    });
    boot.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
